import React, { useMemo, FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import AppLayout, { TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppMainNavbar from '@modules/layout/layout/components/AppMainNavbar';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import AppRightNavContent, { RenderItem } from '@modules/ui/shared/components/AppRightNavContent';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import stripMarkdown from '@modules/ui/shared/utils/stripMarkdown';
import { TestId } from '@modules/utilities/constants/testConstants';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import AppHeading from '@modules/layout/layout/components/AppHeading';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import { OpenAPIPageContent } from '@modules/utilities/pageBuild/content';
import { Typography, Grid } from '@rbx/ui';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import useOpenAPIFilteredOperations from '../hooks/useOpenAPIFilteredOperations';
import OperationNavItem from './OperationNavItem';
import CloudIABanner from '../../CloudIABanner';
import Operation from './Operation';
import FilterBar from './FilterBar';
import useOpenAPIAppContainerStyles from './OpenAPIAppContainer.styles';

type OpenAPIAppContainerProps = {
  content: OpenAPIPageContent;
  openGraphMeta: Partial<TOpenGraphMeta>;
};

const OpenAPIAppContainer: FunctionComponent<React.PropsWithChildren<OpenAPIAppContainerProps>> = ({
  content,
  openGraphMeta,
}) => {
  const { translate } = useTranslation();
  const { classes } = useOpenAPIAppContainerStyles();
  const { getBreadcrumbsItems } = useSideBarContent();
  const { filteredOperations: operations } = useOpenAPIFilteredOperations();

  const metaDescription = useMemo(
    () => (content.description != null ? stripMarkdown(content.description) : null),
    [content.description],
  );

  // Right nav headings
  const headings: HeadingItem[] = useMemo(() => {
    return operations.map(({ operation, method, path }) => {
      const sanitizedPath = OpenAPI.sanitizePath(path);
      const sanitizedSummary = operation.summary?.replace(/<br\s*\/?>/g, ' ');
      return {
        title: sanitizedSummary || operation.operationId || sanitizedPath,
        label: method.toLowerCase(),
        slug: OpenAPI.getOperationDeepLinkFragment(content.document, operation, method, path),
        level: 2, // No plans on introducing more levels, but using 2 to accomodate top-level headings in the future if desired
      };
    });
  }, [operations, content.document]);

  const renderItem: RenderItem = ({ title, label }) => (
    <OperationNavItem method={label as OpenAPI.HttpMethod} title={title} />
  );

  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);

  return (
    <AppLayout
      testid={TestId.OpenAPIContainer}
      pageTitle={content.title}
      pageMetaDescription={metaDescription}
      headings={headings}
      openGraphMeta={openGraphMeta}
      renderBanner={() => <CloudIABanner />}>
      <AppMainNavbar pageTitle={content.title} breadcrumbsItems={breadcrumbsItems} />
      <AppHeading title={content.title} />
      <AppIAHeading title={content.title} breadcrumbsItems={breadcrumbsItems} />
      <AppRightNav content={<AppRightNavContent renderItem={renderItem} />} />
      <AppContent>
        <Grid container direction='column' classes={{ root: classes.container }}>
          {content.description && (
            <Grid item classes={{ root: classes.description }}>
              <Typography variant='body1'>{content.description}</Typography>
            </Grid>
          )}
          <Grid item>
            <FilterBar />
          </Grid>
          <Grid item container direction='column' classes={{ root: classes.operations }}>
            {operations.length > 0 ? (
              operations.map(({ operation, method, path }) => (
                <Grid
                  item
                  key={OpenAPI.getOperationDeepLinkFragment(
                    content.document,
                    operation,
                    method,
                    path,
                  )}
                  id={OpenAPI.getOperationDeepLinkFragment(
                    content.document,
                    operation,
                    method,
                    path,
                  )}>
                  <Operation operation={operation} method={method} path={path} />
                </Grid>
              ))
            ) : (
              <Grid item>
                <Typography variant='body1' color='secondary' style={{ fontStyle: 'italic' }}>
                  {translate('Message.NoOperationsFound')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
};

export default OpenAPIAppContainer;
